import { useState } from 'react';
import { ArrowLeft, Lock, Shield, Eye, Database, Globe, Mail } from 'lucide-react';
import { theme } from '../lib/theme';

const EFFECTIVE_DATE = 'April 21, 2026';
const COMPANY_NAME = 'Trovva Inc.';
const PRODUCT_NAME = 'Trovva';
const SUPPORT_EMAIL = 'support@trovva.ai';
const PRIVACY_EMAIL = 'privacy@trovva.ai';
const WEBSITE = 'https://trovva.ai';

export default function PrivacyPolicy({ onBack }) {
  const sectionStyle = {
    marginBottom: 32,
  };
  const headingStyle = {
    fontSize: 18, fontWeight: 700, color: theme.colors.text,
    marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
  };
  const subHeadingStyle = {
    fontSize: 15, fontWeight: 600, color: theme.colors.text,
    marginBottom: 8, marginTop: 16,
  };
  const paraStyle = {
    fontSize: 14, lineHeight: 1.7, color: theme.colors.textLight,
    marginBottom: 12,
  };
  const listStyle = {
    fontSize: 14, lineHeight: 1.8, color: theme.colors.textLight,
    margin: '8px 0 12px 20px', paddingLeft: 0,
  };
  const tableWrapStyle = {
    overflowX: 'auto', marginBottom: 16,
  };
  const cellStyle = {
    padding: '10px 14px', fontSize: 13, color: theme.colors.textLight,
    borderBottom: `1px solid ${theme.colors.border}`, verticalAlign: 'top',
  };
  const headerCellStyle = {
    ...cellStyle, fontWeight: 700, color: theme.colors.text,
    background: `${theme.colors.primary}08`,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bg,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '16px 24px',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          {onBack && (
            <button onClick={onBack} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              color: theme.colors.primary, fontSize: 14, fontWeight: 500,
            }}>
              <ArrowLeft size={18} /> Back
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryLight})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, color: 'white', fontSize: 16,
            }}>T</div>
            <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>Trovva</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px 80px' }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <Lock size={28} color={theme.colors.primary} />
            <h1 style={{ fontSize: 28, fontWeight: 800, color: theme.colors.text, margin: 0 }}>
              Privacy Policy
            </h1>
          </div>
          <p style={{ fontSize: 14, color: theme.colors.textLight, margin: 0 }}>
            Effective Date: {EFFECTIVE_DATE} &nbsp;|&nbsp; Last Updated: {EFFECTIVE_DATE}
          </p>
        </div>

        {/* Intro */}
        <div style={sectionStyle}>
          <p style={paraStyle}>
            {COMPANY_NAME} ("{PRODUCT_NAME}," "we," "us," or "our") respects your privacy and is committed
            to protecting the personal information you entrust to us. This Privacy Policy explains how we
            collect, use, share, and protect information when you use the {PRODUCT_NAME} platform and related
            services (the "Service").
          </p>
          <p style={paraStyle}>
            This policy applies to all users of the Service, including brokerage administrators ("Subscribers"),
            agents, team members, and visitors to our website. For information about your contacts' and leads'
            data that you store in {PRODUCT_NAME}, see Section 7 (Data You Store About Others).
          </p>
        </div>

        {/* 1. Information We Collect */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <Database size={20} color={theme.colors.primary} />
            1. Information We Collect
          </h2>

          <div style={subHeadingStyle}>1.1 Information You Provide</div>
          <div style={tableWrapStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${theme.colors.border}`, borderRadius: 8 }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Category</th>
                  <th style={headerCellStyle}>Examples</th>
                  <th style={headerCellStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={cellStyle}>Account Information</td>
                  <td style={cellStyle}>Name, email, phone, password, brokerage name, license number</td>
                  <td style={cellStyle}>Account creation, authentication, licensing verification</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Billing Information</td>
                  <td style={cellStyle}>Payment method, billing address</td>
                  <td style={cellStyle}>Payment processing (handled by our payment processor)</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Business Content</td>
                  <td style={cellStyle}>Contact/lead data, transaction records, documents, emails, notes</td>
                  <td style={cellStyle}>Providing CRM, transaction management, and communication features</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Communications</td>
                  <td style={cellStyle}>Messages sent via SMS, email, WhatsApp, voice call recordings/transcripts</td>
                  <td style={cellStyle}>Delivering messages, compliance records, quality assurance</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Uploaded Files</td>
                  <td style={cellStyle}>Documents, forms, images, contracts</td>
                  <td style={cellStyle}>Document management, e-signature workflows, deal rooms</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={subHeadingStyle}>1.2 Information Collected Automatically</div>
          <div style={tableWrapStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${theme.colors.border}` }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Category</th>
                  <th style={headerCellStyle}>Examples</th>
                  <th style={headerCellStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={cellStyle}>Usage Data</td>
                  <td style={cellStyle}>Features used, pages visited, actions taken, timestamps</td>
                  <td style={cellStyle}>Service improvement, analytics, performance optimization</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Device Information</td>
                  <td style={cellStyle}>Browser type, OS, screen resolution, IP address</td>
                  <td style={cellStyle}>Security, troubleshooting, responsive design</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Cookies & Tracking</td>
                  <td style={cellStyle}>Session cookies, authentication tokens</td>
                  <td style={cellStyle}>Session management, authentication persistence</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={subHeadingStyle}>1.3 Information from Third Parties</div>
          <p style={paraStyle}>
            We receive information from third-party integrations that you connect, including lead data from
            Zillow Tech Connect, Facebook Lead Ads, and other lead sources; email metadata from Google and
            Microsoft email integrations; and property data from connected MLS or listing services.
          </p>
        </div>

        {/* 2. How We Use Your Information */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. How We Use Your Information</h2>
          <p style={paraStyle}>We use the information we collect to:</p>
          <ul style={listStyle}>
            <li>Provide, maintain, and improve the Service, including CRM, transaction management, communications, and AI features</li>
            <li>Process payments and manage your subscription</li>
            <li>Send transactional communications (account alerts, billing, feature updates)</li>
            <li>Provide customer support and respond to your inquiries</li>
            <li>Power AI features including Trovva Brain, automated responses, lead scoring, and content generation</li>
            <li>Deliver your outbound communications (SMS, email, WhatsApp, voice calls) via our messaging infrastructure</li>
            <li>Generate analytics and reports on your business performance</li>
            <li>Detect and prevent fraud, abuse, and security threats</li>
            <li>Comply with legal obligations and regulatory requirements</li>
          </ul>
        </div>

        {/* 3. AI Data Processing */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. AI Data Processing</h2>
          <p style={paraStyle}>
            {PRODUCT_NAME}'s AI features process your data to provide intelligent assistance. Specifically:
          </p>
          <ul style={listStyle}>
            <li>Your prompts and queries to the AI Brain are processed by third-party AI model providers (such as Anthropic and OpenAI) under data processing agreements that prohibit training on your data</li>
            <li>AI-generated summaries, suggestions, and drafts are created from your existing data within {PRODUCT_NAME}</li>
            <li>We do not sell your data to AI model providers, and your data is not used to train general-purpose AI models</li>
            <li>Voice call recordings processed through Trovva Brain voice (Vapi) are stored securely and used solely for delivering the voice feature and generating call transcripts</li>
          </ul>
        </div>

        {/* 4. How We Share Your Information */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. How We Share Your Information</h2>
          <p style={paraStyle}>
            We do not sell your personal information. We share information only in the following circumstances:
          </p>

          <div style={subHeadingStyle}>Service Providers</div>
          <p style={paraStyle}>We work with trusted third-party providers who process data on our behalf:</p>
          <div style={tableWrapStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${theme.colors.border}` }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Provider</th>
                  <th style={headerCellStyle}>Purpose</th>
                  <th style={headerCellStyle}>Data Shared</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={cellStyle}>Supabase</td>
                  <td style={cellStyle}>Database hosting, authentication</td>
                  <td style={cellStyle}>All application data (encrypted at rest)</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Twilio</td>
                  <td style={cellStyle}>SMS delivery, voice infrastructure</td>
                  <td style={cellStyle}>Phone numbers, message content</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Meta Cloud API</td>
                  <td style={cellStyle}>WhatsApp messaging</td>
                  <td style={cellStyle}>Phone numbers, message content</td>
                </tr>
                <tr>
                  <td style={cellStyle}>SendGrid</td>
                  <td style={cellStyle}>Email delivery</td>
                  <td style={cellStyle}>Email addresses, email content</td>
                </tr>
                <tr>
                  <td style={cellStyle}>BoldSign</td>
                  <td style={cellStyle}>E-signature processing</td>
                  <td style={cellStyle}>Document content, signer details</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Vapi</td>
                  <td style={cellStyle}>AI voice calling</td>
                  <td style={cellStyle}>Phone numbers, call audio, transcripts</td>
                </tr>
                <tr>
                  <td style={cellStyle}>Stripe (or similar)</td>
                  <td style={cellStyle}>Payment processing</td>
                  <td style={cellStyle}>Billing information (we do not store full card numbers)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={subHeadingStyle}>Other Sharing</div>
          <ul style={listStyle}>
            <li><strong>Within Your Organization:</strong> Data you enter may be visible to other users in your brokerage based on their role and permissions set by your administrator</li>
            <li><strong>Client Portal / Deal Room:</strong> When you share documents or information through the client-facing deal room, the intended recipients will have access to shared materials</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required by law, subpoena, court order, or regulatory request</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction, subject to the same privacy protections</li>
          </ul>
        </div>

        {/* 5. Trovva Pixel & Website Tracking */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <Eye size={20} color={theme.colors.primary} />
            5. Trovva Pixel & Website Tracking
          </h2>
          <p style={paraStyle}>
            If your brokerage administrator installs the Trovva Pixel on your brokerage website, the pixel collects:
          </p>
          <ul style={listStyle}>
            <li>Pages visited on the brokerage website</li>
            <li>Time spent on each page</li>
            <li>Property pages viewed (if configured)</li>
            <li>Visitor identification via URL parameters from {PRODUCT_NAME}-generated links (emails, SMS)</li>
            <li>Anonymous visitor tracking via first-party cookies</li>
          </ul>
          <p style={paraStyle}>
            This data is used solely by the subscribing brokerage to understand lead engagement and is not
            shared with other brokerages, sold to third parties, or used for advertising purposes. Visitors
            to brokerage websites should review the brokerage's own privacy policy for details on tracking
            practices.
          </p>
        </div>

        {/* 6. Cookies */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Cookies & Similar Technologies</h2>
          <p style={paraStyle}>
            {PRODUCT_NAME} uses cookies and similar technologies for:
          </p>
          <ul style={listStyle}>
            <li><strong>Essential cookies:</strong> Authentication, session management, and security (required for the Service to function)</li>
            <li><strong>Analytics cookies:</strong> Understanding how users interact with the Service to improve features and performance</li>
          </ul>
          <p style={paraStyle}>
            We do not use third-party advertising cookies. You can control cookie preferences through your
            browser settings, but disabling essential cookies may prevent you from using the Service.
          </p>
        </div>

        {/* 7. Data You Store About Others */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Data You Store About Others</h2>
          <p style={paraStyle}>
            As a real estate CRM, {PRODUCT_NAME} stores information about your contacts, leads, and clients
            that you input or import. You are the data controller for this information and are responsible for:
          </p>
          <ul style={listStyle}>
            <li>Ensuring you have a lawful basis to store and process your contacts' personal information</li>
            <li>Responding to data subject requests from your contacts (access, deletion, correction)</li>
            <li>Complying with applicable privacy laws (including CCPA, if applicable) regarding your contacts' data</li>
          </ul>
          <p style={paraStyle}>
            {PRODUCT_NAME} acts as a data processor for contact data you store, processing it only on your
            instructions (i.e., to provide the Service features you use).
          </p>
        </div>

        {/* 8. Data Security */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <Shield size={20} color={theme.colors.primary} />
            8. Data Security
          </h2>
          <p style={paraStyle}>
            We implement industry-standard security measures to protect your data, including:
          </p>
          <ul style={listStyle}>
            <li>Encryption in transit (TLS 1.2+) and at rest (AES-256)</li>
            <li>Row-level security (RLS) ensuring data isolation between organizations</li>
            <li>Role-based access controls within each organization</li>
            <li>Regular security assessments and monitoring</li>
            <li>Secure authentication with password hashing and optional multi-factor authentication</li>
          </ul>
          <p style={paraStyle}>
            While we strive to protect your information, no method of transmission over the Internet or
            electronic storage is 100% secure. We cannot guarantee absolute security.
          </p>
        </div>

        {/* 9. Data Retention */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Data Retention</h2>
          <p style={paraStyle}>
            We retain your data for as long as your account is active or as needed to provide the Service.
            After account termination:
          </p>
          <ul style={listStyle}>
            <li>Your Data is available for export for 30 days</li>
            <li>After the export period, data is scheduled for permanent deletion within 90 days</li>
            <li>Certain data may be retained longer as required by law (e.g., billing records, transaction logs for tax purposes)</li>
            <li>Anonymized and aggregated data (which cannot identify you) may be retained indefinitely for analytics and service improvement</li>
          </ul>
        </div>

        {/* 10. Your Rights */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Your Privacy Rights</h2>
          <p style={paraStyle}>
            Depending on your location, you may have the following rights regarding your personal information:
          </p>

          <div style={subHeadingStyle}>All Users</div>
          <ul style={listStyle}>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements</li>
            <li><strong>Data Portability:</strong> Export your data using the built-in export tools at any time</li>
            <li><strong>Opt-Out of Marketing:</strong> Unsubscribe from marketing communications at any time (transactional communications are not affected)</li>
          </ul>

          <div style={subHeadingStyle}>California Residents (CCPA)</div>
          <p style={paraStyle}>
            If you are a California resident, you have the right to: know what personal information we collect
            and how it is used; request deletion of your personal information; opt out of the sale of personal
            information (note: we do not sell personal information); and not be discriminated against for
            exercising these rights.
          </p>

          <div style={subHeadingStyle}>New Jersey Residents</div>
          <p style={paraStyle}>
            In accordance with applicable New Jersey privacy laws, you may exercise the same rights as
            described above. We comply with New Jersey's consumer protection standards.
          </p>

          <p style={paraStyle}>
            To exercise any of these rights, contact us at{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: theme.colors.primary }}>{PRIVACY_EMAIL}</a>.
            We will respond to verified requests within 45 days.
          </p>
        </div>

        {/* 11. Children's Privacy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Children's Privacy</h2>
          <p style={paraStyle}>
            The Service is not intended for use by individuals under 18 years of age. We do not knowingly
            collect personal information from children. If we learn that we have collected information from
            a child under 18, we will take steps to delete that information promptly.
          </p>
        </div>

        {/* 12. International Data */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <Globe size={20} color={theme.colors.primary} />
            12. International Data Transfers
          </h2>
          <p style={paraStyle}>
            {PRODUCT_NAME} is operated from the United States. If you access the Service from outside the
            United States, your information may be transferred to, stored, and processed in the United States
            or other countries where our service providers operate. By using the Service, you consent to such
            transfers.
          </p>
        </div>

        {/* 13. Changes */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Changes to This Policy</h2>
          <p style={paraStyle}>
            We may update this Privacy Policy from time to time. We will notify you of material changes by
            email and/or a prominent notice within the Service at least 15 days before the changes take
            effect. The "Last Updated" date at the top of this policy reflects the most recent revision.
          </p>
        </div>

        {/* Contact */}
        <div style={{
          padding: 24, borderRadius: 12,
          background: `${theme.colors.primary}08`,
          border: `1px solid ${theme.colors.primary}20`,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, marginBottom: 8, marginTop: 0 }}>
            Contact Us About Privacy
          </h2>
          <p style={{ ...paraStyle, margin: 0 }}>
            If you have questions about this Privacy Policy or want to exercise your privacy rights,
            contact us at{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: theme.colors.primary }}>{PRIVACY_EMAIL}</a>.
            You may also write to: {COMPANY_NAME}, Privacy Team, New Jersey, United States.
          </p>
        </div>
      </div>
    </div>
  );
}
